import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { A2HSCampaignContext } from './A2HSCampaignContext';
import { A2HSEvent, a2hsEventEmitter, beforeInstallPromptEvent } from '../utils/a2hsEventListeners';
import type { BeforeInstallPromptEvent } from '../schema/BeforeInstallPromptEvent';
import { deleteA2HSCampaignData } from '../utils/a2hsStorageUtils';
import { getSupportedBrowserConfigs } from '../utils/getSupportedBrowserConfigs';
import { isUserCandidateForA2HSCampaign } from '../utils/isUserCandidateForA2HSCampaign';
import type {
    A2HSCampaignContextValue,
    A2HSCampaignProviderProps,
} from '../schema/A2HSCampaignProvider.types';

/**
 * Context provider to pass the A2HSCampaign related data and setters.
 * Should be consumed higher up in the component hierarchy.
 * @param props
 */
export const A2HSCampaignProvider: React.FC<A2HSCampaignProviderProps> = props => {
    const [isA2HSCampaignTargetUser, setIsA2HSCampaignTargetUser] = useState(false);
    const [savedBeforeInstallPrompt, setBeforeInstallPrompt] =
        useState<BeforeInstallPromptEvent>(beforeInstallPromptEvent);
    const [isA2HSCustomBannerDismissed, setIsA2HSCustomBannerDismissed] = useState(false);

    const endA2HSCampaignForUser = useCallback(() => {
        setIsA2HSCampaignTargetUser(false);
        setBeforeInstallPrompt(null);
        deleteA2HSCampaignData();
    }, []);

    useEffect(() => {
        const saveEvent = (ev: BeforeInstallPromptEvent) => {
            setBeforeInstallPrompt(ev);
        };

        a2hsEventEmitter.on(A2HSEvent.BEFORE_INSTALL_PROMPT, saveEvent);
        return () => {
            a2hsEventEmitter.off(A2HSEvent.BEFORE_INSTALL_PROMPT, saveEvent);
        };
    }, []);

    useEffect(() => {
        a2hsEventEmitter.on(A2HSEvent.APP_INSTALLED, endA2HSCampaignForUser);
        return () => {
            a2hsEventEmitter.off(A2HSEvent.APP_INSTALLED, endA2HSCampaignForUser);
        };
    }, [endA2HSCampaignForUser]);

    useEffect(() => {
        (async () => {
            const isTargetUser = await isUserCandidateForA2HSCampaign(props);
            setIsA2HSCampaignTargetUser(isTargetUser);
        })();
        /* eslint-disable-next-line react-hooks/exhaustive-deps --
         * See aka.ms/mini-lint-errors
         *	> React Hook useEffect has a missing dependency: 'props'. Either include it or remove the dependency array. */
    }, [props.suppressCampaign, props.supportedBrowserConfigs]);

    const contextValue = useMemo<A2HSCampaignContextValue>(
        () => ({
            isA2HSCampaignTargetUser,
            savedBeforeInstallPrompt,
            endA2HSCampaignForUser,
            isA2HSCustomBannerDismissed,
            dismissA2HSCustomBanner: () => setIsA2HSCustomBannerDismissed(true),
        }),
        [
            isA2HSCampaignTargetUser,
            savedBeforeInstallPrompt,
            endA2HSCampaignForUser,
            isA2HSCustomBannerDismissed,
        ]
    );

    return (
        <A2HSCampaignContext.Provider value={contextValue}>
            {props.children}
        </A2HSCampaignContext.Provider>
    );
};

A2HSCampaignProvider.defaultProps = {
    suppressCampaign: false,
    supportedBrowserConfigs: getSupportedBrowserConfigs(),
};
